import { httpApi } from '@api/http.api.ts';
import type { CommonTableRequest } from '@routes/sidebar/admin-center/___shared___/types.ts';
import type {
    CommonRolesRankResponse,
    CommonVesselGroupResponseType,
    CommonVesselGroupType,
    UserType,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/roles/___shared___/types.ts';
import type { EmptyResultsDescription, PaginatedResponse } from '@types';

export type AssessorAccessRequest = CommonTableRequest;
export type AssessorRank = CommonVesselGroupType;

export interface AssessorUserResponse {
    id: number;
    firstName: string;
    lastName: string;
    rankOrTitleName: string;
    userType: UserType;
}

export interface AssessorAccess {
    id: number;
    modifiedById: number;
    modifiedByTenantAlias: string;
    modifiedBy: string;
    userType: UserType;
    version: string | null;
    vesselGroups: CommonVesselGroupType[];
    assessorRanks?: AssessorRank[];
    shoreUsers?: AssessorUserResponse[];
    assesseeRanks: AssessorRank[];
}

export interface UpsertAssessorAccessRequest {
    userType: UserType;
    vesselGroupIds: number[];
    assessorRankIds?: number[];
    shoreUserIds?: number[];
    assesseeRankIds: number[];
}

export type AssessorAccessResponse = PaginatedResponse<AssessorAccess, EmptyResultsDescription>;
export type AssessorVesselGroupsSearchResponse = PaginatedResponse<
    CommonVesselGroupResponseType,
    EmptyResultsDescription
>;
export type AssessorRanksSearchResponse = PaginatedResponse<CommonRolesRankResponse, EmptyResultsDescription>;
export type AssessorUsersSearchResponse = PaginatedResponse<AssessorUserResponse, EmptyResultsDescription>;

const getAssessorAccessBaseUrl = (tenantAlias: string) => `admin-center/tenants/${tenantAlias}/rbac/assessor-access`;

export const getApiAssessorAccess = (
    tenantAlias: string,
    payload: AssessorAccessRequest,
): Promise<AssessorAccessResponse> =>
    httpApi
        .post<AssessorAccessResponse>(`${getAssessorAccessBaseUrl(tenantAlias)}/search`, payload)
        .then(({ data }) => data);

export const createApiAssessorAccess = (
    tenantAlias: string,
    payload: UpsertAssessorAccessRequest,
): Promise<AssessorAccess> =>
    httpApi.post<AssessorAccess>(getAssessorAccessBaseUrl(tenantAlias), payload).then(({ data }) => data);

export const updateApiAssessorAccess = (
    tenantAlias: string,
    accessId: number,
    payload: UpsertAssessorAccessRequest,
): Promise<AssessorAccess> =>
    httpApi
        .put<AssessorAccess>(`${getAssessorAccessBaseUrl(tenantAlias)}/${accessId}`, payload)
        .then(({ data }) => data);

export const deleteApiAssessorAccess = (tenantAlias: string, accessId: number): Promise<any> =>
    httpApi.delete<any>(`${getAssessorAccessBaseUrl(tenantAlias)}/${accessId}`).then(({ data }) => data);

export const getApiAssessorVesselGroups = (
    tenantAlias: string,
    payload: AssessorAccessRequest,
): Promise<AssessorVesselGroupsSearchResponse> =>
    httpApi
        .post<AssessorVesselGroupsSearchResponse>(
            `${getAssessorAccessBaseUrl(tenantAlias)}/vessel-groups/search`,
            payload,
        )
        .then(({ data }) => data);

export const getApiAssessorRanks = (
    tenantAlias: string,
    payload: AssessorAccessRequest,
): Promise<AssessorRanksSearchResponse> =>
    httpApi
        .post<AssessorRanksSearchResponse>(`${getAssessorAccessBaseUrl(tenantAlias)}/ranks/search`, payload)
        .then((res) => res.data);

export const getApiAssessorUsers = (
    tenantAlias: string,
    payload: AssessorAccessRequest,
): Promise<AssessorUsersSearchResponse> =>
    httpApi
        .post<AssessorUsersSearchResponse>(`${getAssessorAccessBaseUrl(tenantAlias)}/users/search`, payload)
        .then((res) => res.data);
